import { useCallback } from 'react';

import { Dispatch } from '@reduxjs/toolkit';

import {
  IHydrateCartInput,
  IMenuSelectionInput,
  Platform,
  ServiceMode,
  useHydrateCartMutation,
} from 'generated/graphql-gateway';
import { useAuthContext } from 'state/auth';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { IMinimalCartEntry } from 'state/global-state/models/ordering/cart/cart.types';
import { useServiceModeContext } from 'state/service-mode';
import { useStoreContext } from 'state/store';
import { isWeb } from 'utils/environment';
import {
  buildMinimalCartEntriesLookup,
  findMatchingCartEntry,
  mapHydratedCart,
  mapServiceModeToCartServiceMode,
} from 'utils/minimal-cart';

import { useCart } from './use-cart';

export interface IUseMinimalCart {
  cartEntries: IMinimalCartEntry[];
  addItemToMinimalCart: (cartEntry: IMinimalCartEntry) => void;
  emptyMinimalCart: () => void;
  hydrateMinimalCart: (entries?: IMinimalCartEntry[]) => Promise<boolean>;
}

/**
 * Expose the cart state and the utility methods to mutate it.
 *
 * When an action to mutate the cart is triggered, it manages the Redux cart actions, logs the events,
 * coordinates with other parts of the code, and dispatches actions on related slices.
 */
export const useMinimalCart = (): IUseMinimalCart => {
  const dispatch = useAppDispatch();
  const minimalCartEntries = useAppSelector(selectors.ordering.selectMinimalCartEntries);
  const { store } = useStoreContext();
  const storeId = store?.number;
  const { serviceMode } = useServiceModeContext();
  const { isAuthenticated } = useAuthContext();
  const [hydrateCart] = useHydrateCartMutation();
  const { addItemToCart, updateCartEntry } = useCart();

  // Adds a single cartEntry to the cart
  const addItemToMinimalCart = useCallback<IUseMinimalCart['addItemToMinimalCart']>(
    minimalCartEntry => {
      dispatch(actions.ordering.addMinimalCartEntry(minimalCartEntry));
    },
    [dispatch]
  );

  const emptyMinimalCart = useCallback<IUseMinimalCart['emptyMinimalCart']>(() => {
    dispatch(actions.ordering.resetMinimalCart());
  }, [dispatch]);

  const hydrateMinimalCart = useCallback(
    async (entries?: IMinimalCartEntry[]) => {
      if (!storeId || !serviceMode || !isAuthenticated) {
        return false;
      }
      return hydrateEntries(
        entries ?? minimalCartEntries,
        storeId,
        serviceMode,
        hydrateCart,
        addItemToCart,
        updateCartEntry,
        dispatch
      );
    },
    [
      addItemToCart,
      dispatch,
      hydrateCart,
      isAuthenticated,
      minimalCartEntries,
      serviceMode,
      storeId,
      updateCartEntry,
    ]
  );

  return {
    cartEntries: minimalCartEntries,
    addItemToMinimalCart,
    emptyMinimalCart,
    hydrateMinimalCart,
  };
};

// Had to extract this to a pure function to avoid a circular dep between use-cart and use-minimal-cart
export async function hydrateEntries(
  cartEntries: IMinimalCartEntry[],
  storeId: string,
  serviceMode: ServiceMode,
  hydrateCart: ReturnType<typeof useHydrateCartMutation>[0],
  addItemToCart: ReturnType<typeof useCart>['addItemToCart'],
  updateCartEntry: ReturnType<typeof useCart>['updateCartEntry'],
  dispatch: Dispatch
): Promise<boolean> {
  const channel = isWeb ? Platform.WEB : Platform.APP;
  const menuSelections: IMenuSelectionInput[] = cartEntries.map(entry => ({
    id: entry.id,
    quantity: entry.quantity,
    menuSelections: entry.menuSelections,
  }));
  const input: IHydrateCartInput = {
    cart: { menuSelections },
    serviceMode: mapServiceModeToCartServiceMode(serviceMode),
    storeId,
  };

  try {
    const hydrationResult = await hydrateCart({
      variables: { channel, input },
    });

    if (hydrationResult.errors) {
      throw hydrationResult.errors;
    }

    const hydratedCartEntries = mapHydratedCart(
      hydrationResult?.data?.hydrateCart.cartEntries ?? [],
      buildMinimalCartEntriesLookup(cartEntries)
    );

    hydratedCartEntries.forEach(cartEntry => {
      const matchingMinimalCartEntry = findMatchingCartEntry(cartEntries, cartEntry);
      if (matchingMinimalCartEntry?.isEdit) {
        updateCartEntry(cartEntry, cartEntry.cartId, true, true);
      } else {
        addItemToCart(cartEntry, undefined, false, true);
        if (matchingMinimalCartEntry?.rewardBenefitId) {
          dispatch(
            actions.loyalty.applyReward({
              rewardBenefitId: matchingMinimalCartEntry.rewardBenefitId,
              cartId: cartEntry.cartId,
              quantity: 1,
            })
          );
        }
      }
    });
    dispatch(actions.ordering.resetMinimalCart());
    return true;
  } catch (error) {
    dispatch(actions.ordering.resetMinimalCart());
    return false;
  }
}
